/**
 * Value formatting utilities for human-readable display.
 */

interface UnitConfig {
  readonly decimals: number;
  readonly symbol: string;
  readonly subunits: readonly { readonly threshold: bigint; readonly decimals: number; readonly symbol: string }[];
}

const ETHEREUM_UNITS: UnitConfig = {
  decimals: 18,
  symbol: 'ETH',
  subunits: [
    { threshold: 1_000_000_000n, decimals: 9, symbol: 'gwei' },
    { threshold: 1n, decimals: 0, symbol: 'wei' },
  ],
};

const BITCOIN_UNITS: UnitConfig = {
  decimals: 8,
  symbol: 'BTC',
  subunits: [
    { threshold: 1n, decimals: 0, symbol: 'sat' },
  ],
};

const SOLANA_UNITS: UnitConfig = {
  decimals: 9,
  symbol: 'SOL',
  subunits: [
    { threshold: 1n, decimals: 0, symbol: 'lamports' },
  ],
};

const CHAIN_UNITS: Record<string, UnitConfig> = {
  ethereum: ETHEREUM_UNITS,
  bitcoin: BITCOIN_UNITS,
  solana: SOLANA_UNITS,
};

/**
 * Format a raw value (in the smallest denomination) to human-readable form.
 * Examples:
 *   formatValue(1000000000000000000n, 'ethereum') → "1.0 ETH"
 *   formatValue(1000000000n, 'ethereum') → "1.0 gwei"
 *   formatValue(100000000n, 'bitcoin') → "1.0 BTC"
 */
export function formatValue(rawValue: bigint, chain: string): string {
  const units = CHAIN_UNITS[chain];
  if (!units) {
    return rawValue.toString();
  }

  const divisor = 10n ** BigInt(units.decimals);

  // Always show in the main unit (ETH/BTC/SOL) for clarity.
  // Only use subunits for very small values where the main unit
  // would show as "0.0" (less than 0.0001 of the main unit).
  const minMainUnit = divisor / 10000n; // 0.0001 ETH/BTC/SOL

  if (rawValue === 0n || rawValue >= minMainUnit) {
    return `${formatDecimal(rawValue, units.decimals)} ${units.symbol}`;
  }

  // Very small values: use subunits
  for (const sub of units.subunits) {
    if (rawValue >= sub.threshold) {
      if (sub.decimals === 0) {
        return `${rawValue.toString()} ${sub.symbol}`;
      }
      return `${formatDecimal(rawValue, sub.decimals)} ${sub.symbol}`;
    }
  }

  // Fallback: show in main unit
  return `${formatDecimal(rawValue, units.decimals)} ${units.symbol}`;
}

/**
 * Format a bigint value with decimal places.
 * formatDecimal(1500000000000000000n, 18) → "1.5"
 * formatDecimal(1000000000000000000n, 18) → "1.0"
 */
export function formatDecimal(value: bigint, decimals: number): string {
  if (decimals === 0) {
    return value.toString();
  }

  const divisor = 10n ** BigInt(decimals);
  const wholePart = value / divisor;
  const fractionalPart = value % divisor;

  if (fractionalPart === 0n) {
    return `${wholePart.toString()}.0`;
  }

  const fracStr = fractionalPart.toString().padStart(decimals, '0');
  // Trim trailing zeros but keep at least one decimal place
  const trimmed = fracStr.replace(/0+$/, '') || '0';

  return `${wholePart.toString()}.${trimmed}`;
}

/**
 * Truncate an address for display.
 * "0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045" → "0xd8dA...6045"
 */
export function truncateAddress(address: string, prefixLen = 6, suffixLen = 4): string {
  if (address.length <= prefixLen + suffixLen + 3) {
    return address;
  }
  return `${address.slice(0, prefixLen)}...${address.slice(-suffixLen)}`;
}

/**
 * Format a gas value for display.
 */
export function formatGas(gas: bigint): string {
  return gas.toLocaleString?.() ?? gas.toString();
}

/**
 * Format gas price in gwei.
 */
export function formatGasPrice(weiValue: bigint): string {
  return `${formatDecimal(weiValue, 9)} gwei`;
}
